// Core
import { useState } from 'react';
import { useQuery } from 'react-query';
import { Box, Button, Flex, Image, Grid, Text, Skeleton, useColorMode } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';

// Assets
import { apiService } from 'services/ApiService';
import { validateLoadMore } from 'services/Utils';
import dailyWheel from 'assets/img/daily.png';
import { ReactComponent as DailyCursorIcon } from 'assets/svg/daily-cursor.svg';
import { ReactComponent as DailyMapIcon } from 'assets/svg/daily-map.svg';

// Components
import Giveaways from './Giveaway';
import EmptyList from './EmptyList';
import Countdown from 'components/Timers/Countdown';
import { StarsWhiteIcon } from '../../../components/Icons/Icons';

const DailyWinners = ({ userData }) => {
	const limit = 8;
    const [offset, setOffset] = useState(0);
    const [data, setData] = useState([]);
    const [dataMeta, setDataMeta] = useState(); 
    const [errorMessage, setErrorMessage] = useState();

    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const { isLoading } = useQuery({
        queryKey: ['dailyWinners', offset],
        queryFn: async () => {
            const response = await apiService.getGiveaways({ limit, offset, status: 'finished', type: 'member', declareSetting: 'auto' });
            
            if(response.statusCode === 400){
                setErrorMessage(response.message);
                return true;
            } else {
                setErrorMessage();
            }

            if(offset === 0){
                setData(response.data);
            } else {
                setData([...data, ...response.data]);
            }
            setDataMeta(response.meta);
            return true;
        },
        refetchOnWindowFocus: false,
        retry: false
    });

    const { data: nextDaily, isLoading: isNextLoading } = useQuery({
        queryKey: ['dailyNextWinners'],
        queryFn: async () => {
            const response = await apiService.getGiveaways({ 
                status: 'active', 
                type: 'member',
                fromDeclareDate: new Date().toISOString(),
                declareSetting: 'auto' });
            return response.data;
        },
        refetchOnWindowFocus: false,
        retry: false
    });

    const loadMore = async () => {
        setOffset(offset + limit);
    };

    return (
    	<>
    	{ isLoading && data?.length === 0 ? ( 
            <Grid mt={{base: '24px', md: '40px'}} templateColumns={{ base: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }} gap="24px" templateRows='1fr'>
                <Skeleton borderRadius="16px" h="200px" />
                <Skeleton borderRadius="16px" h="200px" />
                <Skeleton borderRadius="16px" h="200px" />
            </Grid>
        ) : !isLoading && data?.length === 0 ? 
            (
                <EmptyList 
                    title="No giveaways winners"
                    descriptions={ errorMessage ? errorMessage : 'Unfortunately, it’s empty here for now. Please wait for the giveaways to see the giveaway winners.'}
                    />
        ) : (
            <>
    	        <Grid mt={{base: '24px', md: '40px'}} templateColumns={{ base: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }} gap="24px" templateRows='1fr'>
    			    { nextDaily?.length > 0 && userData?.billingState === 'SA' && window.location.hostname !== 'lmctplus.com' &&
                        <NavLink to="/giveaways/daily/wheel">
                            <Box
                                h="100%"
                                borderRadius="12px" 
                                position="relative" 
                                minH="148px"
                                background="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                                overflow="hidden">

                                <Image src={dailyWheel} 
                                    objectPosition="bottom"
                                    alt="Daily Wheel" w="100%" h="100%" objectFit="contain" position="absolute" />

                                { nextDaily && nextDaily[0]?.declareDate && (
                                    <Box w="100%">
                                    <Countdown
                                        fontSize="15px"
                                        endDate={ nextDaily[0]?.declareDate }
                                        declareDate={ nextDaily[0]?.declareDate }
                                        p="0 6px"
                                        right="unset"
                                        position="relative"
                                        width="fit-content"
                                        m="0 auto"
                                      />
                                    </Box>
                                )}

                                <Box position="absolute" w="100%" top="0px" textAlign="center">
                                    <Flex gap="4px" justifyContent="center" mt="36px" alignItems="center">
                                        <StarsWhiteIcon fontSize="16px" overflow="visible" />
                                        <Text variant="header" mt="4px" fontSize="24px" color="white">Daily $1000</Text>
                                        <StarsWhiteIcon fontSize="16px" overflow="visible" />
                                    </Flex>
                                    <Text 
                                        variant="header" 
                                        fontSize="20px" 
                                        mt="5px" 
                                        lineHeight="23px"
                                        p="0 30px"
                                        color="white">SOUTH AUSTRALIAN GIVEAWAYS</Text>
                                </Box>

                                <Box
                                    className="animation-dailycursor"
                                    right="16px"
                                    bottom="16px"
                                    position="absolute"
                                    padding="5px"
                                    borderRadius="100px"
                                    border="1.5px solid #FFF"
                                    background="rgba(255, 255, 255, 0.10)"
                                    boxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
                                    >
                                    <DailyCursorIcon />
                                </Box>
                            </Box>
                        </NavLink>
                    }

                    { data?.map((item, index) => (
    			        <Giveaways key={index} item={item} type="daily" userData={userData} />
    			    ))}	
    	        </Grid>

                { validateLoadMore(data, dataMeta) && (
                    <Button 
                        isLoading={isLoading}  
                        variant={ isDark ? 'button-md-g' : 'button-md' }  
                        maxW="220px" 
                        onClick={loadMore} 
                        left="50%"
                        transform="translate(-50%, 0)"
                        m="24px auto">
                      Load More
                    </Button>
                )}
            </>
	    )}
	    </>
    );
};


export default DailyWinners;